import React, { useState, useEffect } from "react";
import { Button } from "@heroui/button";
import { Input } from "@heroui/input";

interface OrderStatusUpdateProps {
  currentStatusId: number;
  onUpdate: (statusId: number, notes?: string) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
}

// These would typically come from an API call to get available statuses
const orderStatuses = [
  { id: 1, name: "New", color: "#3498db" },
  { id: 2, name: "Processing", color: "#f39c12" },
  { id: 3, name: "Ready", color: "#2ecc71" },
  { id: 4, name: "Out for Delivery", color: "#9b59b6" },
  { id: 5, name: "Delivered", color: "#27ae60" },
  { id: 6, name: "Picked Up", color: "#27ae60" },
  { id: 7, name: "Cancelled", color: "#e74c3c" },
];

const OrderStatusUpdate: React.FC<OrderStatusUpdateProps> = ({
  currentStatusId,
  onUpdate,
  onCancel,
  loading,
}) => {
  const [selectedStatusId, setSelectedStatusId] =
    useState<number>(currentStatusId);
  const [notes, setNotes] = useState<string>("");
  const [statusFlow, setStatusFlow] = useState<any[]>([]);

  // Determine valid status transitions based on current status
  useEffect(() => {
    // In a real app, this logic would be more complex and possibly server-driven
    const determineStatusFlow = () => {
      const allStatuses = [...orderStatuses];

      // Keep only statuses that make sense for the current flow
      // This is a simple implementation - a real app would have more complex rules
      switch (currentStatusId) {
        case 1: // New
          return allStatuses.filter((s) => [2, 3, 7].includes(s.id));
        case 2: // Processing
          return allStatuses.filter((s) => [3, 7].includes(s.id));
        case 3: // Ready
          return allStatuses.filter((s) => [4, 6, 7].includes(s.id));
        case 4: // Out for Delivery
          return allStatuses.filter((s) => [5, 7].includes(s.id));
        case 5: // Delivered
          return []; // Terminal state
        case 6: // Picked Up
          return []; // Terminal state
        case 7: // Cancelled
          return []; // Terminal state
        default:
          return allStatuses;
      }
    };

    setStatusFlow(determineStatusFlow());
  }, [currentStatusId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate(selectedStatusId, notes.trim() || undefined);
  };

  // Get style color based on status
  const getStatusColor = (statusId: number) => {
    const status = orderStatuses.find((s) => s.id === statusId);

    return status ? status.color : "#718096";
  };

  // Get current status name
  const getCurrentStatusName = () => {
    const status = orderStatuses.find((s) => s.id === currentStatusId);

    return status ? status.name : "Unknown";
  };

  // No available transitions
  if (statusFlow.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Cannot Update Status
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              The current status "{getCurrentStatusName()}" is a terminal state
              and cannot be changed.
            </p>
            <div className="flex justify-end">
              <Button color="primary" onPress={onCancel}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full overflow-hidden">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Update Order Status
          </h3>

          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Current Status
              </label>
              <div
                className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-md text-gray-900 dark:text-white"
                style={{
                  borderLeft: `4px solid ${getStatusColor(currentStatusId)}`,
                }}
              >
                {getCurrentStatusName()}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                New Status
              </label>
              <div className="grid grid-cols-2 gap-2">
                {statusFlow.map((status) => (
                  <div
                    key={status.id}
                    className={`
                      px-3 py-2 rounded-md cursor-pointer border-2 text-center
                      ${
                        selectedStatusId === status.id
                          ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300"
                          : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900"
                      }
                    `}
                    style={{
                      borderLeftWidth: "4px",
                      borderLeftColor: status.color,
                    }}
                    onClick={() => setSelectedStatusId(status.id)}
                  >
                    {status.name}
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notes (Optional)
              </label>
              <Input
                as="textarea"
                className="w-full"
                placeholder="Add notes about this status change"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button disabled={loading} variant="light" onPress={onCancel}>
                Cancel
              </Button>
              <Button color="primary" isLoading={loading} type="submit">
                Update Status
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrderStatusUpdate;
